import { z } from 'zod';
export function jvmArgError(arg: string): string | undefined {
  const reject = (reason: string) => arg + ': ' + reason;
  if (!arg || arg.length > 256 || /[\s'"\0]/u.test(arg))
    return reject('1〜256 文字で、空白・改行・引用符・NUL は指定できません');
  if (
    /^-Xm[xsn]/.test(arg) ||
    /^-XX:(?:[+-])?(?:MaxHeapSize|InitialHeapSize|MinHeapSize|MaxRAM|InitialRAMPercentage|MinRAMPercentage)/.test(arg)
  )
    return reject('メモリはメモリの設定で変更してください');
  if (
    /^(?:@|-javaagent|-agentpath|-agentlib|-Xbootclasspath|-XX:(?:[+-])?(?:OnError|OnOutOfMemoryError|Flags|VMOptionsFile))/.test(
      arg,
    )
  )
    return reject('コマンド実行・コードの差し込みにつながるため指定できません');
  if (
    !/^(?:-XX:.+|-D.+|-Xss.+|-Xlog.*|-Xshare.*|-ea(?::.*)?|-da(?::.*)?|--(?:add-opens|add-exports|enable-native-access)=.+)$/.test(
      arg,
    )
  )
    return reject('許可された Java の起動引数ではありません');
  return undefined;
}
export const JvmSettingsSchema = z
  .object({
    flags: z.enum(['aikar', 'none']),
    extraArgs: z
      .array(
        z.string().superRefine((arg, ctx) => {
          const message = jvmArgError(arg);
          if (message) ctx.addIssue({ code: 'custom', message });
        }),
      )
      .max(64, '追加の引数は最大 64 個です')
      .default([]),
  })
  .strict();
export type JvmSettings = z.infer<typeof JvmSettingsSchema>;
export function defaultJvm(family: string): JvmSettings {
  return { flags: ['paper', 'purpur'].includes(family) ? 'aikar' : 'none', extraArgs: [] };
}
export function effectiveJvm(server: { software: { family: string }; jvm?: JvmSettings }): JvmSettings {
  return JvmSettingsSchema.parse(server.jvm ?? defaultJvm(server.software.family));
}
export function withJvmDefaults<T extends { software: { family: string }; jvm?: JvmSettings }>(
  value: T,
): T & { jvm: JvmSettings } {
  return { ...value, jvm: effectiveJvm(value) };
}
export const aikarFlags = [
  '-XX:+UseG1GC',
  '-XX:+ParallelRefProcEnabled',
  '-XX:MaxGCPauseMillis=200',
  '-XX:+UnlockExperimentalVMOptions',
  '-XX:+DisableExplicitGC',
  '-XX:+AlwaysPreTouch',
  '-XX:G1NewSizePercent=30',
  '-XX:G1MaxNewSizePercent=40',
  '-XX:G1HeapRegionSize=8M',
  '-XX:G1ReservePercent=20',
  '-XX:G1HeapWastePercent=5',
  '-XX:G1MixedGCCountTarget=4',
  '-XX:InitiatingHeapOccupancyPercent=15',
  '-XX:G1MixedGCLiveThresholdPercent=90',
  '-XX:G1RSetUpdatingPauseTimePercent=5',
  '-XX:SurvivorRatio=32',
  '-XX:+PerfDisableSharedMem',
  '-XX:MaxTenuringThreshold=1',
];

/** JVM が落ちたときの記録（hs_err）を、保存される crash-reports/ に書かせる。追加引数で上書きできるよう先に置く */
export const panelJvmArgs = ['-XX:ErrorFile=crash-reports/hs_err_pid%p.log'];
export function jvmArgs(server: {
  resources: { heapMb: number; heapMinMb?: number };
  software: { family: string };
  jvm?: JvmSettings;
}): string[] {
  const jvm = effectiveJvm(server);
  return [
    '-Xms' + (server.resources.heapMinMb ?? server.resources.heapMb) + 'M',
    '-Xmx' + server.resources.heapMb + 'M',
    ...(jvm.flags === 'aikar' ? aikarFlags : []),
    ...panelJvmArgs,
    ...jvm.extraArgs,
  ];
}
export const LaunchViewSchema = z.object({
  javaExecutable: z.string(),
  javaMajor: z.number().int().positive().optional(),
  memoryArgs: z.array(z.string()),
  flagArgs: z.array(z.string()),
  extraArgs: z.array(z.string()),
  launchArgs: z.array(z.string()),
  args: z.array(z.string()),
  command: z.string(),
  jvm: JvmSettingsSchema,
});
export type LaunchView = z.infer<typeof LaunchViewSchema>;
export function shellQuote(arg: string): string {
  return "'" + arg.replaceAll("'", "'\\''") + "'";
}
export function launchView(
  server: Parameters<typeof jvmArgs>[0],
  javaExecutable = 'java',
  launchArgs = ['-jar', 'server.jar', 'nogui'],
  javaMajor?: number,
): LaunchView {
  const jvm = effectiveJvm(server);
  const memoryArgs = jvmArgs(server).slice(0, 2);
  const flagArgs = jvm.flags === 'aikar' ? [...aikarFlags] : [];
  const args = [...memoryArgs, ...flagArgs, ...panelJvmArgs, ...jvm.extraArgs, ...launchArgs];
  return {
    javaExecutable,
    javaMajor,
    memoryArgs,
    flagArgs,
    extraArgs: jvm.extraArgs,
    launchArgs,
    args,
    jvm,
    command: [javaExecutable, ...args].map(shellQuote).join(' '),
  };
}
